/**
 * Public "magic link" upload page for clients whose Telegram video was
 * over 20 MB. The token in the URL IS the authentication — no login
 * required. Mobile-first because 90% of clients open it on iPhone.
 *
 * Flow
 * ----
 * 1. On mount: GET /upload-link/{token} → validate + show client name.
 * 2. Client picks 1..N files with <input multiple>.
 * 3. Each file is uploaded sequentially via `uploadViaMagicLink` (direct
 *    browser → Dropbox, chunked). Progress bar per file.
 * 4. Success screen once all done.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { uploadViaMagicLink } from "../lib/dropboxUpload";
import { HistorySection } from "./upload/HistorySection";
import {
  UploadCloud,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Video,
  Image as ImageIcon,
  X,
  Plus,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// iOS Safari's "Photo Library" picker silently limits <input multiple>
// to ONE video per tap, even though it allows multi-select for photos.
// We detect iOS to show a dedicated hint that primes the client to
// tap "Aggiungi altro video" once per clip.
const IS_IOS = typeof navigator !== "undefined" && (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
);

const STATE_LOADING = "loading";
const STATE_READY = "ready";
const STATE_INVALID = "invalid";

export default function UploadLink() {
  const { token } = useParams();
  const [state, setState] = useState(STATE_LOADING);
  const [info, setInfo] = useState(null); // { client_name, group_id, expires_at, ... }
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]); // [{ id, file, status, progress, error, result }]
  const [uploading, setUploading] = useState(false);
  const wakeLockRef = useRef(null);
  const uploadingRef = useRef(false);
  // ONE page load = ONE session_id = ONE Dropbox group. Regenerated every
  // time the client (re-)opens the /upload/:token URL. The backend uses
  // this to group all clips of the same video together — no more
  // time-window guessing, no more accidental merges when the client
  // uploads two videos back-to-back without closing the tab. See
  // v1.33 handoff notes in PRD.md.
  const sessionIdRef = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  );

  // Keep the phone screen ON while uploading: on iPhone/Android, screen
  // lock suspends the page JS and kills the upload mid-flight (that's
  // how big videos used to "vanish"). Best-effort — older browsers just
  // ignore it.
  const acquireWakeLock = useCallback(async () => {
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch (_) {
      /* not supported / low battery — never block the upload */
    }
  }, []);
  const releaseWakeLock = useCallback(() => {
    try {
      wakeLockRef.current?.release();
    } catch (_) {
      /* noop */
    }
    wakeLockRef.current = null;
  }, []);

  // Re-acquire the wake lock if the user briefly switches app and comes
  // back (the OS auto-releases it on visibility loss).
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && uploadingRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [acquireWakeLock]);

  // Warn before closing the tab mid-upload.
  useEffect(() => {
    if (!uploading) return undefined;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [uploading]);

  // Validate token on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/upload-link/${token}`);
        setInfo(data);
        setState(STATE_READY);
      } catch (e) {
        setError(apiError(e));
        setState(STATE_INVALID);
      }
    })();
  }, [token]);

  const onFileSelect = useCallback((e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const now = Date.now();
    setFiles((prev) => [
      ...prev,
      ...picked.map((f, i) => ({
        id: `${now}-${i}-${f.name}`,
        file: f,
        label: "", // v1.40 — client-typed nickname, optional
        status: "pending", // pending | uploading | done | error
        progress: 0,
        error: null,
        result: null,
      })),
    ]);
    // Reset input so the same file can be re-selected after removal
    e.target.value = "";
  }, []);

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const updateLabel = (id, label) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, label } : f)));
  };

  // v1.40 — drag & drop reorder. Disabled while uploading to avoid the
  // sequence number changing mid-flight (backend uses whatever the client
  // last sent per row). Long-press activation on touch keeps the picker
  // easy to scroll on mobile.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || uploading) return;
    setFiles((prev) => {
      const oldIx = prev.findIndex((f) => f.id === active.id);
      const newIx = prev.findIndex((f) => f.id === over.id);
      if (oldIx === -1 || newIx === -1) return prev;
      return arrayMove(prev, oldIx, newIx);
    });
  };

  const startUpload = async () => {
    if (uploading || !files.length) return;
    setUploading(true);
    uploadingRef.current = true;
    await acquireWakeLock();
    // Sequential upload — parallel would saturate mobile connections
    // and Dropbox rate-limits chunk sessions per token anyway.
    // Snapshot the ordered list ONCE so sequence numbers stay stable
    // even if the user manages to trigger a reorder mid-loop (shouldn't
    // happen because we disable drag while uploading, but be safe).
    const snapshot = [...files];
    for (let i = 0; i < snapshot.length; i++) {
      const f = snapshot[i];
      const sequence = i + 1; // 1-based
      if (f.status === "done" || f.status === "uploaded_unregistered") continue;
      setFiles((prev) =>
        prev.map((x) => (x.id === f.id ? { ...x, status: "uploading", progress: 0, error: null } : x)),
      );
      try {
        const result = await uploadViaMagicLink({
          magicToken: token,
          file: f.file,
          sessionId: sessionIdRef.current,
          sequence,
          label: (f.label || "").trim(),
          onProgress: (p) => {
            setFiles((prev) =>
              prev.map((x) => (x.id === f.id ? { ...x, progress: p } : x)),
            );
          },
        });
        setFiles((prev) =>
          prev.map((x) =>
            x.id === f.id ? { ...x, status: "done", progress: 100, result } : x,
          ),
        );
      } catch (e) {
        // Special case: the bytes ARE on Dropbox, only the portal
        // registration failed. The backend reconciler will attach it
        // automatically within the hour — don't scare the client and
        // don't let them re-upload a duplicate.
        if (e && e.uploadedToDropbox) {
          setFiles((prev) =>
            prev.map((x) =>
              x.id === f.id
                ? { ...x, status: "uploaded_unregistered", progress: 100, error: null }
                : x,
            ),
          );
        } else {
          setFiles((prev) =>
            prev.map((x) =>
              x.id === f.id ? { ...x, status: "error", error: String(e.message || e) } : x,
            ),
          );
        }
      }
    }
    uploadingRef.current = false;
    releaseWakeLock();
    setUploading(false);
  };

  // --- Renders ---------------------------------------------------------
  if (state === STATE_LOADING) {
    return (
      <FullPage>
        <Loader2 className="h-8 w-8 animate-spin text-[var(--mp-ink)] mx-auto mb-3" />
        <p className="text-sm text-[var(--mp-muted)]">Verifica del link…</p>
      </FullPage>
    );
  }

  if (state === STATE_INVALID) {
    return (
      <FullPage>
        <div className="rounded-3xl bg-red-50 border-2 border-red-300 p-6 max-w-md mx-auto text-center">
          <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-3" />
          <h1 className="font-display text-2xl mb-2">Link non valido</h1>
          <p className="text-sm text-red-900 leading-relaxed">{error}</p>
          <p className="text-xs text-[var(--mp-muted)] mt-4">
            Chiedi allo staff di generarti un nuovo link scrivendo &quot;video&quot;
            al bot Telegram.
          </p>
        </div>
      </FullPage>
    );
  }

  const allDone =
    files.length > 0 &&
    files.every((f) => f.status === "done" || f.status === "uploaded_unregistered");
  const anyUnregistered = files.some((f) => f.status === "uploaded_unregistered");
  const anyError = files.some((f) => f.status === "error");
  const pendingCount = files.filter(
    (f) => f.status === "pending" || f.status === "error",
  ).length;

  return (
    <FullPage>
      <div className="max-w-lg mx-auto space-y-5">
        <header className="text-center">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-2">
            <UploadCloud className="h-3 w-3" />
            Marketing Parrucchieri
          </div>
          <h1
            data-testid="upload-link-title"
            className="font-display text-3xl sm:text-4xl tracking-tight"
          >
            Ciao {info?.client_name || "Cliente"} 👋
          </h1>
          <p className="text-sm text-[var(--mp-muted)] mt-2 leading-relaxed">
            Carica qui i tuoi video e foto: vanno dritti su Dropbox, nella
            cartella giusta, <strong>senza limiti di dimensione</strong>.
          </p>
          {info?.group_id && (
            <div
              data-testid="upload-link-group-hint"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-300 px-3 py-1 text-[11px] font-semibold text-blue-900"
            >
              <CheckCircle2 className="h-3 w-3" />
              Verranno associati automaticamente al gruppo giusto
            </div>
          )}
        </header>

        {/* ONE VIDEO PER SESSION — mission-critical instruction. If the
            client uploads clips of two different videos in the same
            page load, the montage team can't tell which clip belongs to
            which final video. Sticky-visible, red, unmistakable. */}
        <div
          data-testid="upload-link-one-video-banner"
          className="rounded-3xl bg-red-600 text-white border-2 border-red-800 p-5 shadow-brutal-sm"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-white text-red-600 grid place-items-center">
              <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg sm:text-xl leading-tight">
                Un video alla volta!
              </div>
              <p className="text-sm mt-1.5 leading-relaxed">
                Carica tutte le clip di <strong>un solo video</strong>, poi{" "}
                <strong>chiudi questa pagina</strong> e riaprila da Telegram per il video
                successivo. Se carichi tutto insieme, <strong>non capiremo come montare il
                video</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Picker — two separate inputs (video / foto) because on iOS
            a mixed accept forces the Photo Library picker to single-file
            selection for videos. Splitting them makes the flow explicit
            and lets photos multi-select correctly. */}
        {files.length === 0 ? (
          <div className="grid grid-cols-2 gap-3" data-testid="upload-link-picker-group">
            <PickerBox
              testId="upload-link-picker-video"
              accept="video/*"
              icon={<Video className="h-8 w-8 mx-auto text-[var(--mp-ink)] mb-2" />}
              title="Video"
              subtitle="Nessun limite di dimensione"
              onChange={onFileSelect}
              disabled={uploading}
            />
            <PickerBox
              testId="upload-link-picker-photo"
              accept="image/*"
              multiple
              icon={<ImageIcon className="h-8 w-8 mx-auto text-[var(--mp-ink)] mb-2" />}
              title="Foto"
              subtitle="Puoi sceglierne tante"
              onChange={onFileSelect}
              disabled={uploading}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="upload-link-picker-group">
            <AddMoreBox
              testId="upload-link-add-video"
              accept="video/*"
              label="Aggiungi video"
              onChange={onFileSelect}
              disabled={uploading}
            />
            <AddMoreBox
              testId="upload-link-add-photo"
              accept="image/*"
              multiple
              label="Aggiungi foto"
              onChange={onFileSelect}
              disabled={uploading}
            />
          </div>
        )}

        {IS_IOS && !uploading && (
          <div
            data-testid="upload-link-ios-hint"
            className="rounded-2xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 leading-relaxed"
          >
            <strong>Su iPhone</strong>: aggiungi <strong>un video alla volta</strong> —
            tappa di nuovo &quot;Aggiungi video&quot; per ognuno. Le foto invece
            puoi selezionarle tutte insieme.
          </div>
        )}

        {/* File list */}
        {files.length > 0 && (
          <div className="space-y-2" data-testid="upload-link-file-list">
            <div className="text-[11px] text-[var(--mp-muted)] leading-relaxed">
              💡 Tocca <strong>e tieni premuto</strong> l&apos;icona <GripVertical className="inline h-3 w-3" /> per riordinare · Dai un nome opzionale a ogni clip
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={files.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {files.map((f, i) => (
                  <FileRow
                    key={f.id}
                    f={f}
                    sequence={i + 1}
                    canRemove={!uploading && f.status !== "done" && f.status !== "uploaded_unregistered"}
                    canReorder={!uploading}
                    canRename={!uploading && f.status !== "done" && f.status !== "uploaded_unregistered"}
                    onRemove={() => removeFile(f.id)}
                    onLabelChange={(v) => updateLabel(f.id, v)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* CTA */}
        {files.length > 0 && pendingCount > 0 && !allDone && (
          <button
            data-testid="upload-link-start-btn"
            onClick={startUpload}
            disabled={uploading}
            className="w-full h-14 rounded-full bg-[var(--mp-ink)] hover:bg-black disabled:opacity-50 text-white font-semibold text-base transition-colors"
          >
            {uploading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Caricamento in corso…
              </span>
            ) : (
              <>Carica {pendingCount} {pendingCount === 1 ? "file" : "file"}</>
            )}
          </button>
        )}

        {/* Keep-alive hint while uploading */}
        {uploading && (
          <div
            data-testid="upload-link-keepalive-hint"
            className="rounded-2xl bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900 text-center leading-relaxed"
          >
            <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
            <strong>Tieni lo schermo acceso e resta su questa pagina</strong>{" "}
            finché non vedi la spunta verde — se il telefono si blocca il
            caricamento si interrompe.
          </div>
        )}

        {/* Success */}
        {allDone && (
          <div
            data-testid="upload-link-success"
            className="rounded-3xl bg-green-50 border-2 border-green-300 p-5 text-center"
          >
            <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="font-display text-xl">Fatto! ✨</div>
            <p className="text-sm text-green-900 mt-1">
              {files.length} {files.length === 1 ? "video caricato" : "video caricati"}.
              Puoi chiudere questa pagina — lo staff ha già ricevuto tutto.
            </p>
            {anyUnregistered && (
              <p
                data-testid="upload-link-unregistered-note"
                className="text-xs text-amber-800 mt-2 leading-relaxed"
              >
                Alcuni file risultano su Dropbox ma la conferma finale non è
                andata a buon fine: verranno agganciati automaticamente al
                portale entro un&apos;ora. Non serve ricaricarli.
              </p>
            )}
          </div>
        )}
        {anyError && !allDone && (
          <div className="rounded-2xl bg-red-50 border border-red-300 p-3 text-xs text-red-900">
            <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
            Alcuni file non sono stati caricati. Riprova cliccando di nuovo
            &quot;Carica&quot; — verranno ritentati automaticamente.
          </div>
        )}

        {/* v1.43 — "Cosa hai già mandato" gallery. Only rendered for
            permanent links: 24h expiring links don't have enough
            history to justify the section. */}
        {info?.permanent && <HistorySection token={token} refreshKey={allDone ? files.length : 0} />}

        <footer className="text-center text-[10px] text-[var(--mp-muted)] pt-4">
          Marketing Parrucchieri ·{" "}
          {info?.permanent
            ? "Il tuo link personale — sempre valido 📌"
            : info?.expires_at
              ? `Link valido fino al ${new Date(info.expires_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`
              : "Link valido per 24h"}
        </footer>
      </div>
    </FullPage>
  );
}

// --- Sub-components ---------------------------------------------------

function PickerBox({ testId, accept, multiple = false, icon, title, subtitle, onChange, disabled }) {
  return (
    <label
      data-testid={testId}
      className="block rounded-3xl border-2 border-dashed border-[var(--mp-ink)] bg-white p-6 text-center cursor-pointer hover:bg-neutral-50 transition-colors"
    >
      {icon}
      <div className="font-display text-base leading-tight">{title}</div>
      <div className="text-[11px] text-[var(--mp-muted)] mt-1 leading-tight">
        {subtitle}
      </div>
      <input
        type="file"
        accept={accept}
        {...(multiple ? { multiple: true } : {})}
        className="hidden"
        onChange={onChange}
        disabled={disabled}
        data-testid={`${testId}-input`}
      />
    </label>
  );
}

function AddMoreBox({ testId, accept, multiple = false, label, onChange, disabled }) {
  return (
    <label
      data-testid={testId}
      className="block rounded-2xl border-2 border-dashed border-[var(--mp-ink)]/40 bg-white p-4 text-center cursor-pointer hover:bg-neutral-50 hover:border-[var(--mp-ink)] transition-colors"
    >
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--mp-ink)]">
        <Plus className="h-4 w-4" />
        {label}
      </div>
      <input
        type="file"
        accept={accept}
        {...(multiple ? { multiple: true } : {})}
        className="hidden"
        onChange={onChange}
        disabled={disabled}
        data-testid={`${testId}-input`}
      />
    </label>
  );
}

function FullPage({ children }) {
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4"
      style={{ background: "var(--mp-dash-bg, #fafaf7)" }}
    >
      <div className="w-full">{children}</div>
    </div>
  );
}

function FileRow({ f, sequence, canRemove, canReorder, canRename, onRemove, onLabelChange }) {
  const isImage = f.file.type.startsWith("image/");
  const Icon = isImage ? ImageIcon : Video;
  const sizeMB = (f.file.size / (1024 * 1024)).toFixed(1);
  const sortable = useSortable({ id: f.id, disabled: !canReorder });
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = sortable;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`upload-link-file-row-${f.id}`}
      className={`rounded-2xl border ${
        isDragging ? "border-[var(--mp-ink)] shadow-lg" : "border-[var(--mp-border)]"
      } bg-white p-3 flex items-start gap-2 touch-manipulation`}
    >
      {/* Drag handle */}
      <button
        type="button"
        data-testid={`upload-link-drag-${f.id}`}
        aria-label="Riordina"
        disabled={!canReorder}
        {...attributes}
        {...listeners}
        className={`flex-shrink-0 h-10 w-6 rounded-lg grid place-items-center text-[var(--mp-muted)] ${
          canReorder ? "cursor-grab active:cursor-grabbing hover:bg-neutral-100" : "opacity-30"
        }`}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Sequence badge + icon */}
      <div className="flex-shrink-0 flex flex-col items-center gap-1">
        <div
          data-testid={`upload-link-seq-${f.id}`}
          className="h-6 min-w-[24px] px-1.5 rounded-full bg-[var(--mp-ink)] text-white text-[11px] font-bold grid place-items-center"
        >
          {String(sequence).padStart(2, "0")}
        </div>
        <div className="h-8 w-8 rounded-xl bg-neutral-100 grid place-items-center">
          <Icon className="h-4 w-4 text-[var(--mp-ink)]" />
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-1.5">
        {/* Optional label input */}
        {canRename ? (
          <input
            type="text"
            data-testid={`upload-link-label-${f.id}`}
            value={f.label}
            onChange={(e) => onLabelChange(e.target.value)}
            placeholder="Nome della clip (opzionale)"
            maxLength={60}
            className="w-full h-8 px-3 rounded-full bg-neutral-50 border border-[var(--mp-border)] text-sm placeholder:text-neutral-400 focus:outline-none focus:border-[var(--mp-ink)] focus:bg-white transition-colors"
          />
        ) : f.label ? (
          <div className="text-sm font-semibold text-[var(--mp-ink)] truncate">
            {f.label}
          </div>
        ) : null}
        <div className="text-[11px] text-[var(--mp-muted)] truncate">
          {f.file.name}
        </div>
        <div className="text-[11px] text-[var(--mp-muted)] flex items-center gap-2">
          <span>{sizeMB} MB</span>
          {f.status === "done" && (
            <span className="text-green-700 font-semibold">✓ Caricato</span>
          )}
          {f.status === "uploaded_unregistered" && (
            <span className="text-amber-700 font-semibold">
              ✓ Su Dropbox (registrazione automatica in corso)
            </span>
          )}
          {f.status === "error" && (
            <span className="text-red-700 font-semibold">Errore: {f.error}</span>
          )}
          {f.status === "uploading" && (
            <span className="text-blue-700 font-semibold">{f.progress}%</span>
          )}
        </div>
        {(f.status === "uploading" || f.status === "done" || f.status === "uploaded_unregistered") && (
          <div className="mt-1 h-1 rounded-full bg-neutral-100 overflow-hidden">
            <div
              className={`h-full transition-all ${f.status === "uploading" ? "bg-blue-500" : "bg-green-500"}`}
              style={{ width: `${f.progress}%` }}
            />
          </div>
        )}
      </div>

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-neutral-400 hover:text-red-600 p-1 flex-shrink-0"
          data-testid={`upload-link-remove-${f.id}`}
          aria-label="Rimuovi"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
